import type { Locale } from 'date-fns'
import type { KeyboardEvent } from 'react'
import type { DateFormat } from '../fields/date-field/DateField.types'
import type { DatePickerRenderTriggerSlot } from './date-picker-trigger/DatePickerTrigger.types'
import type { DatePickerCustomTriggerProps, DatePickerDefaultTriggerProps } from './DatePicker.types'
import { da, de, enGB, fr, nl, nlBE, sv } from 'date-fns/locale'
import { ValidationStatuses } from '../fields/base-field'

export const countryCodes: Record<string, Locale> = {
  'nl-NL': nl,
  'nl-BE': nlBE,
  'en-GB': enGB,
  'fr-FR': fr,
  'fr-BE': fr,
  'de-DE': de,
  'da-DK': da,
  'sv-SE': sv,
}
interface DatePickerCalendarCommonProps {
  /**
   * Additional CSS class name applied to the field wrapper.
   */
  className?: string

  /**
   * Label text displayed above the date picker trigger.
   */
  label?: string

  /**
   * Helper text displayed below the label.
   */
  helpText?: string

  /**
   * Validation status of the field.
   */
  status?: ValidationStatuses

  /**
   * Feedback text displayed below the field.
   */
  feedbackText?: string

  /**
   * Placeholder text displayed when no date is selected.
   */
  placeholder?: string

  /**
   * Whether the date picker is disabled and cannot be interacted with.
   */
  disabled?: boolean

  /**
   * Whether the calendar popover is open.
   */
  isOpen: boolean

  /**
   * Minimum selectable date.
   */
  minDate?: Date | null

  /**
   * Maximum selectable date.
   */
  maxDate?: Date | null

  /**
   * Dates that cannot be selected.
   */
  excludeDates?: Date[]

  /**
   * Dates that can be selected.
   */
  includeDates?: Date[]

  /**
   * Locale string (e.g., 'en-GB', 'nl-NL') to determine language and date formatting.).
   * @default 'en-GB'
   */
  locale?: keyof typeof countryCodes

  /**
   * Date display format used by the trigger value.
   * Supported formats are shared with DateField.
   */
  dateFormat?: DateFormat

  /**
   * Accessible label for the next month button.
   */
  nextMonthAriaLabel: string

  /**
   * Accessible label for the previous month button.
   */
  previousMonthAriaLabel: string

  /**
   * Whether the calendar closes after selecting a date.
   */
  shouldCloseOnSelect: boolean

  /**
   * Whether the calendar allows selecting a date range.
   */
  selectsRange?: boolean

  /**
   * Currently selected date, or range start date in range mode.
   */
  selected: Date | null

  /**
   * Selected range start date.
   */
  startDate?: Date | null

  /**
   * Selected range end date.
   */
  endDate?: Date | null

  /**
   * Called when the selected date or range changes.
   */
  onChange: (value: Date | [Date | null, Date | null] | null) => void

  /**
   * Called when a calendar day is selected.
   */
  onSelect?: () => void

  /**
   * Called when the calendar should open.
   */
  onOpen: () => void

  /**
   * Called when the calendar should close.
   */
  onClose: () => void

  /**
   * Called when a key is pressed on the trigger or calendar input.
   */
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void

  /**
   * Whether to show week numbers in the calendar.
   */
  showWeekNumbers?: boolean

  /**
   * Label for the week number column when showWeekNumbers is enabled.
   */
  weekLabel?: string

  /**
   * Number of months displayed side by side in the calendar.
   */
  monthsShown?: number

  /**
   * Whether the calendar renders a visual-only highlighted range around the selected date.
   */
  highlightRange?: boolean
}

export type DatePickerCalendarProps =
  | (DatePickerCalendarCommonProps & { inline?: false } & (DatePickerDefaultTriggerProps | DatePickerCustomTriggerProps))
  | (DatePickerCalendarCommonProps & { inline: true, calendarAriaLabel?: string, renderTriggerSlot?: DatePickerRenderTriggerSlot })
